import React, { useState, useEffect } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import {
  heightPercentageToDP as hp,
} from 'react-native-responsive-screen';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import CustomText from '../components/CustomText';
import { getIsAdmin, getIsUserAdmin, getIsOrgAdmin, getIsMoneta } from '../util/storage';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const Footer = (props) => {
  const { t } = useTranslation('common');

  const route = useRoute();
  const navigation = useNavigation();
  const { isWeb, screenWidth } = useResponsiveStyles();
  const insets = useSafeAreaInsets();

  // Adjust footer elements for viewing the web portal on smaller devices
  const scale = screenWidth < 800 ? 0.8 : 1;

  // V3 footer icon size (slightly larger for better touch targets)
  const iconSize = 26 * scale;

  // Verify admin status from storage, not URL params
  const [verifiedAdmin, setVerifiedAdmin] = useState(false);
  const [verifiedUserAdmin, setVerifiedUserAdmin] = useState(false);
  const [verifiedOrgAdmin, setVerifiedOrgAdmin] = useState(false);
  const [verifiedMoneta, setVerifiedMoneta] = useState(false);

  useEffect(() => {
    const verifyRoles = async () => {
      // For noLogin (QR code) users, trust props since they don't have stored roles
      if (props?.noLogin) {
        setVerifiedAdmin(props.admin === true || props.admin === 'true');
        setVerifiedUserAdmin(props.userAdmin === true || props.userAdmin === 'true');
        setVerifiedOrgAdmin(props.orgAdmin === true || props.orgAdmin === 'true');
        return;
      }

      // For logged-in users, verify from storage
      const storedAdmin = await getIsAdmin();
      const storedUserAdmin = await getIsUserAdmin();
      const storedOrgAdmin = await getIsOrgAdmin();
      const storedMoneta = await getIsMoneta();
      setVerifiedAdmin(storedAdmin === true);
      setVerifiedUserAdmin(storedUserAdmin === true);
      setVerifiedOrgAdmin(storedOrgAdmin === true);
      setVerifiedMoneta(storedMoneta === true);
    };
    verifyRoles();
  }, [props?.noLogin, props?.admin, props?.userAdmin, props?.orgAdmin]);

  // V3 color scheme
  const defaultColor = AppStyles.colorSet.textSecondary;
  const activeColor = AppStyles.colorSet.brandPrimary;
  // Use darker text for active labels (gold text has poor contrast on light bg)
  const activeLabelColor = AppStyles.colorSet.textPrimary;

  // Helper to check if route is active
  const isActive = (routeNames) => {
    if (Array.isArray(routeNames)) {
      return routeNames.includes(route.name);
    }
    return route.name === routeNames;
  };

  // If noApp is true, render an empty footer or minimal footer
  if (props && props.noApp) {
    return (
      <View style={styles.containerMinimal} />
    );
  }

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]} accessibilityRole="tablist">
      {/* Submit Report */}
      <Pressable
        style={({ hovered, focused }) => [styles.navItem, (hovered || focused) && styles.navItemHovered]}
        onPress={() => navigation.navigate('SubmitReport', { ...props, admin: verifiedAdmin, userAdmin: verifiedUserAdmin, orgAdmin: verifiedOrgAdmin })}
        accessibilityRole="tab"
        accessibilityLabel={t('nav.submitReport')}
        accessibilityState={{ selected: isActive('SubmitReport') }}
      >
        {({ hovered, focused }) => (
          <>
            <Feather
              name="send"
              size={iconSize}
              color={isActive('SubmitReport') || hovered || focused ? activeColor : defaultColor}
            />
            <CustomText
              size={12}
              font="medium"
              style={[styles.navLabel, { color: isActive('SubmitReport') || hovered || focused ? activeLabelColor : defaultColor }]}
            >
              {screenWidth < 500 ? t('nav.submitShort') : t('nav.submitReport')}
            </CustomText>
          </>
        )}
      </Pressable>

      {/* View Reports */}
      {props && !props.noLogin && (
        <Pressable
          style={({ hovered, focused }) => [styles.navItem, (hovered || focused) && styles.navItemHovered]}
          onPress={() => {
            let page = verifiedAdmin ? 'AdminFilterHome' : 'PreviousReport';
            navigation.navigate(page, { ...props, admin: verifiedAdmin, userAdmin: verifiedUserAdmin, orgAdmin: verifiedOrgAdmin });
          }}
          accessibilityRole="tab"
          accessibilityLabel={t('nav.viewReports')}
          accessibilityState={{ selected: isActive(['PreviousReport', 'AdminFilterHome']) }}
        >
          {({ hovered, focused }) => (
            <>
              <Feather
                name="inbox"
                size={iconSize}
                color={isActive(['PreviousReport', 'AdminFilterHome']) || hovered || focused ? activeColor : defaultColor}
              />
              <CustomText
                size={12}
                font="medium"
                style={[styles.navLabel, { color: isActive(['PreviousReport', 'AdminFilterHome']) || hovered || focused ? activeLabelColor : defaultColor }]}
              >
                {screenWidth < 500 ? t('nav.reportsShort') : t('nav.viewReports')}
              </CustomText>
            </>
          )}
        </Pressable>
      )}

      {/* Organization Settings (Admin only) - Tag management for all admins, full org management for OrgAdmins */}
      {props && !props.noLogin && verifiedAdmin && (
        <Pressable
          style={({ hovered, focused }) => [styles.navItem, (hovered || focused) && styles.navItemHovered]}
          onPress={() => navigation.navigate('OrganizationManagement', { ...props, admin: verifiedAdmin, userAdmin: verifiedUserAdmin, orgAdmin: verifiedOrgAdmin })}
          accessibilityRole="tab"
          accessibilityLabel={verifiedOrgAdmin ? t('nav.organizationSettings') : t('nav.manageTags')}
          accessibilityState={{ selected: isActive(['OrganizationManagement']) }}
        >
          {({ hovered, focused }) => (
            <>
              <Feather
                name={verifiedOrgAdmin ? 'settings' : 'tag'}
                size={iconSize}
                color={isActive(['OrganizationManagement']) || hovered || focused ? activeColor : defaultColor}
              />
              <CustomText
                size={12}
                font="medium"
                style={[styles.navLabel, { color: isActive(['OrganizationManagement']) || hovered || focused ? activeLabelColor : defaultColor }]}
              >
                {verifiedOrgAdmin
                  ? screenWidth < 500
                    ? t('nav.settingsShort')
                    : t('nav.orgSettingsShort')
                  : screenWidth < 500
                    ? t('nav.tagsShort')
                    : t('nav.manageTags')}
              </CustomText>
            </>
          )}
        </Pressable>
      )}

      {/* Analytics */}
      <Pressable
        style={({ hovered, focused }) => [styles.navItem, (hovered || focused) && styles.navItemHovered]}
        onPress={() => navigation.navigate('AnalyticsScreen', { ...props, admin: verifiedAdmin, userAdmin: verifiedUserAdmin, orgAdmin: verifiedOrgAdmin })}
        accessibilityRole="tab"
        accessibilityLabel={t('nav.analytics')}
        accessibilityState={{ selected: isActive('AnalyticsScreen') }}
      >
        {({ hovered, focused }) => (
          <>
            <Feather
              name="bar-chart-2"
              size={iconSize}
              color={isActive('AnalyticsScreen') || hovered || focused ? activeColor : defaultColor}
            />
            <CustomText
              size={12}
              font="medium"
              style={[styles.navLabel, { color: isActive('AnalyticsScreen') || hovered || focused ? activeLabelColor : defaultColor }]}
            >
              {t('nav.analytics')}
            </CustomText>
          </>
        )}
      </Pressable>

      {/* User Management (Admin + UserAdmin on web desktop only) */}
      {props && !props.noLogin && isWeb && screenWidth >= 800 && verifiedAdmin && verifiedUserAdmin && (
        <Pressable
          style={({ hovered, focused }) => [styles.navItem, (hovered || focused) && styles.navItemHovered]}
          onPress={() => navigation.navigate('UserManagement', { ...props, admin: verifiedAdmin, userAdmin: verifiedUserAdmin, orgAdmin: verifiedOrgAdmin })}
          accessibilityRole="tab"
          accessibilityLabel={t('nav.userManagement')}
          accessibilityState={{ selected: isActive('UserManagement') }}
        >
          {({ hovered, focused }) => (
            <>
              <Feather
                name="users"
                size={iconSize}
                color={isActive('UserManagement') || hovered || focused ? activeColor : defaultColor}
              />
              <CustomText
                size={12}
                font="medium"
                style={[styles.navLabel, { color: isActive('UserManagement') || hovered || focused ? activeLabelColor : defaultColor }]}
              >
                {t('nav.userManagement')}
              </CustomText>
            </>
          )}
        </Pressable>
      )}

      {/* Moneta Admin (Moneta role only) */}
      {props && !props.noLogin && verifiedMoneta && (
        <Pressable
          style={({ hovered, focused }) => [styles.navItem, (hovered || focused) && styles.navItemHovered]}
          onPress={() => navigation.navigate('MonetaDashboard', { ...props, admin: verifiedAdmin, userAdmin: verifiedUserAdmin, orgAdmin: verifiedOrgAdmin })}
          accessibilityRole="tab"
          accessibilityLabel={t('nav.monetaAdmin')}
          accessibilityState={{ selected: isActive(['MonetaDashboard', 'DrawingManagement', 'DrawingDetails', 'CreateDrawing', 'CreateParentOrg']) }}
        >
          {({ hovered, focused }) => (
            <>
              <Feather
                name="award"
                size={iconSize}
                color={isActive(['MonetaDashboard', 'DrawingManagement', 'DrawingDetails', 'CreateDrawing', 'CreateParentOrg']) || hovered || focused ? activeColor : defaultColor}
              />
              <CustomText
                size={12}
                font="medium"
                style={[styles.navLabel, { color: isActive(['MonetaDashboard', 'DrawingManagement', 'DrawingDetails', 'CreateDrawing', 'CreateParentOrg']) || hovered || focused ? activeLabelColor : defaultColor }]}
              >
                {screenWidth < 500 ? 'Moneta' : t('nav.monetaAdmin')}
              </CustomText>
            </>
          )}
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    backgroundColor: AppStyles.stateSet.hover,
    borderTopWidth: 1,
    borderTopColor: AppStyles.colorSet.cardBorder,
    paddingVertical: hp(1),
    paddingHorizontal: 4,
    width: '100%',
  },
  containerMinimal: {
    backgroundColor: AppStyles.stateSet.hover,
    borderTopWidth: 1,
    borderTopColor: AppStyles.colorSet.cardBorder,
    minHeight: 20,
  },
  navItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    cursor: 'pointer',
    borderRadius: 10,
  },
  // Web hover/focus feedback for footer nav tabs — gold tint (the footer's
  // resting background is already stateSet.hover gray, so a gray tint wouldn't show)
  navItemHovered: {
    backgroundColor: 'rgba(255, 196, 81, 0.14)',
  },
  navLabel: {
    marginTop: 4,
    textAlign: 'center',
  },
});

export default Footer;
